/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { CustomXAxisTick } from '../../components/CustomXAxisTick';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

interface Project {
  id: string;
  name: string;
  budget?: number;
  tranche_plan?: any[];
  company_id: string;
  implementation_type?: 'ngo_led' | 'direct';
}

interface FundRequest {
  id: string;
  project_id: string;
  ngo_id: string;
  tranche_number: number;
  amount: number;
  purpose: string;
  status: string;
  attachments?: string[];
  clarification_request?: string;
  rejected_reason?: string;
  created_at: string;
  projects?: { name: string; implementation_type?: string };
  ngos?: { legal_name: string };
}

interface FundDisbursement {
  id: string;
  project_id: string;
  ngo_id: string;
  amount: number;
  released_at: string;
  notes?: string;
  projects?: { name: string; implementation_type?: string };
  ngos?: { legal_name: string };
  company_entities?: { name: string };
  fund_request_id?: string;
  routing_method?: string;
}

interface AdminExpense {
  id: string;
  company_id: string;
  entity_id?: string;
  expense_date: string;
  amount: number;
  category: string;
  description?: string;
  created_at: string;
  company_entities?: { name: string };
}

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
});

// Compact formatter for graph labels
const compactCurrencyFormatter = (value: number) => {
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(0)}k`;
  return `₹${value}`;
};

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6b7280'];

const CompanyFinance: React.FC = () => {
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [fundRequests, setFundRequests] = useState<FundRequest[]>([]);
  const [disbursements, setDisbursements] = useState<FundDisbursement[]>([]);
  const [companyProfile, setCompanyProfile] = useState<any>(null);
  const [receipts, setReceipts] = useState<any[]>([]);
  const [companyEntities, setCompanyEntities] = useState<any[]>([]);
  const [foundationContributions, setFoundationContributions] = useState<any[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [activeTab, setActiveTab] = useState<'budgets' | 'requests' | 'disbursements' | 'receipts' | 'uc'>('budgets');
  const [companyCsrMandate, setCompanyCsrMandate] = useState<number | null>(null);
  const [adminExpenses, setAdminExpenses] = useState<AdminExpense[]>([]);
  const [newAdminExpense, setNewAdminExpense] = useState({ date: new Date().toISOString().split('T')[0], amount: '', category: 'Travel', description: '', entity_id: '' });
  const [isSavingAdminExpense, setIsSavingAdminExpense] = useState(false);
  const [newContribution, setNewContribution] = useState({ date: new Date().toISOString().split('T')[0], amount: '', entity_id: '', notes: '' });
  const [isSavingContribution, setIsSavingContribution] = useState(false);
  const [csrNotification, setCsrNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [newEntity, setNewEntity] = useState({ name: '', csr_mandate: '', admin_budget: '', primary_account_budget: '', entity_budget: '' });
  const [entityLogo, setEntityLogo] = useState<File | null>(null);
  const [entitySaving, setEntitySaving] = useState(false);
  const [editingEntityId, setEditingEntityId] = useState<string | null>(null);
  const [primaryAdminBudgetInput, setPrimaryAdminBudgetInput] = useState<string>('');
  const [foundationSaving, setFoundationSaving] = useState(false);
  const [showAllDisbursements, setShowAllDisbursements] = useState(false);

  const handleEditClick = (entity: any) => {
    setNewEntity({
      name: entity.name,
      csr_mandate: entity.csr_mandate?.toString() || '',
      admin_budget: entity.admin_budget?.toString() || '',
      primary_account_budget: entity.primary_account_budget?.toString() || '',
      entity_budget: entity.entity_budget?.toString() || ''
    });
    setEditingEntityId(entity.id);
    setEntityLogo(null); // Clear file input
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top where form is
  };

  const cancelEdit = () => {
    setNewEntity({ name: '', csr_mandate: '', admin_budget: '', primary_account_budget: '', entity_budget: '' });
    setEditingEntityId(null);
    setEntityLogo(null);
  };


  const handleSaveEntity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEntity.name.trim()) return;

    setEntitySaving(true);
    setError(null);

    try {
      let logoUrl = null;
      if (entityLogo) {
        // Create unique file name
        const fileExt = entityLogo.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `${companyId}/entities/${fileName}`;

        // Upload logo
        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(filePath, entityLogo);

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('company-documents')
          .getPublicUrl(filePath);

        logoUrl = publicUrl;
      }

      const entityData: any = {
        name: newEntity.name.trim(),
        admin_budget: newEntity.admin_budget ? Number(newEntity.admin_budget) : 0,
        primary_account_budget: newEntity.primary_account_budget ? Number(newEntity.primary_account_budget) : 0,
        entity_budget: newEntity.entity_budget ? Number(newEntity.entity_budget) : 0,
      };

      // Auto-calculate total mandate
      entityData.csr_mandate = entityData.admin_budget + entityData.primary_account_budget + entityData.entity_budget;


      if (logoUrl) {
        entityData.logo_url = logoUrl;
      }

      if (editingEntityId) {
        // Update existing entity
        const { data, error: updateError } = await supabase
          .from('company_entities')
          .update(entityData)
          .eq('id', editingEntityId)
          .select()
          .single();

        if (updateError) throw updateError;

        setCompanyEntities(prev => prev.map(e => e.id === editingEntityId ? data : e));
        setCsrNotification({ type: 'success', message: 'Company updated successfully' });
      } else {
        // Insert new entity
        entityData.company_id = companyId;
        const { data, error: insertError } = await supabase
          .from('company_entities')
          .insert(entityData)
          .select()
          .single();

        if (insertError) throw insertError;

        setCompanyEntities(prev => [...prev, data]);
        setCsrNotification({ type: 'success', message: 'Company added successfully' });
      }

      cancelEdit();
      setTimeout(() => setCsrNotification(null), 3000);
    } catch (err: any) {
      console.error('Error saving entity:', err);
      setError(err.message || 'Failed to save entity. Note: adding an entity requires logo_url column.');
    } finally {
      setEntitySaving(false);
    }
  };

  const handleDeleteEntity = async (id: string) => {
    const entityToDelete = companyEntities.find(e => e.id === id);
    const warningMessage = `Warning: Are you sure you want to delete this company "${entityToDelete?.name || 'this entity'}"?\n\n` +
      `Deleting this entity will PERMANENTLY DELETE any connected data, including:\n` +
      `- Fund Disbursements\n` +
      `- Administrative Expenses\n` +
      `- Compliance Documents\n\n` +
      `This cannot be undone.`;

    if (!window.confirm(warningMessage)) return;

    try {
      const { error: deleteError } = await supabase
        .from('company_entities')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setCompanyEntities(prev => prev.filter(e => e.id !== id));
      setCsrNotification({ type: 'success', message: 'Company deleted successfully' });
      setTimeout(() => setCsrNotification(null), 3000);
    } catch (err: any) {
      console.error('Error deleting entity:', err);
      setError(err.message || 'Failed to delete entity');
    }
  };

  const handleSaveAdminExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId || !newAdminExpense.amount || isNaN(Number(newAdminExpense.amount))) return;

    setIsSavingAdminExpense(true);
    setError(null);

    try {
      const expenseData = {
        company_id: companyId,
        entity_id: newAdminExpense.entity_id || null, // null means it belongs to the main company
        expense_date: newAdminExpense.date,
        amount: Number(newAdminExpense.amount),
        category: newAdminExpense.category,
        description: newAdminExpense.description
      };

      const { data, error: insertError } = await supabase
        .from('company_admin_expenses')
        .insert(expenseData)
        .select()
        .single();

      if (insertError) throw insertError;

      const entityMatch = companyEntities.find((e: any) => e.id === data.entity_id);
      const newExpenseWithEntity = {
        ...data,
        company_entities: entityMatch ? { name: entityMatch.name } : undefined
      };

      setAdminExpenses(prev => [(newExpenseWithEntity as unknown as AdminExpense), ...prev].sort((a, b) => new Date(b.expense_date).getTime() - new Date(a.expense_date).getTime()));
      setNewAdminExpense({ date: new Date().toISOString().split('T')[0], amount: '', category: 'Travel', description: '', entity_id: '' });
      setCsrNotification({ type: 'success', message: 'Admin expense added successfully' });
      setTimeout(() => setCsrNotification(null), 3000);
    } catch (err: any) {
      console.error('Error saving admin expense:', err);
      setError(err.message || 'Failed to save admin expense');
    } finally {
      setIsSavingAdminExpense(false);
    }
  };

  const handleSaveFoundationContribution = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId || !newContribution.amount || isNaN(Number(newContribution.amount))) return;
    setIsSavingContribution(true);

    try {
      const { data: savedData, error } = await supabase
        .from('foundation_contributions')
        .insert([{
          company_id: companyId,
          entity_id: newContribution.entity_id || null,
          amount: parseFloat(newContribution.amount),
          contribution_date: newContribution.date,
          notes: newContribution.notes
        }])
        .select('*, company_entities(name)')
        .single();

      if (error) throw error;

      setFoundationContributions(prev => [savedData, ...prev]);
      setCsrNotification({ type: 'success', message: `${companyProfile?.setup_type === 'single' ? 'Contribution' : 'Foundation contribution'} recorded successfully!` });
      setNewContribution({ date: new Date().toISOString().split('T')[0], amount: '', entity_id: '', notes: '' });
      setTimeout(() => setCsrNotification(null), 3000);
    } catch (err: any) {
      console.error('Error saving contribution:', err);
      setError(err.message || 'Failed to save contribution');
    } finally {
      setIsSavingContribution(false);
    }
  };

  const handleDeleteAdminExpense = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('company_admin_expenses')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setAdminExpenses(prev => prev.filter(e => e.id !== id));
      setCsrNotification({ type: 'success', message: 'Expense deleted successfully' });
      setTimeout(() => setCsrNotification(null), 3000);
    } catch (err: any) {
      console.error('Error deleting expense:', err);
      setError(err.message || 'Failed to delete expense');
    }
  };

  const handleDeleteDisbursement = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this fund release record?')) return;

    try {
      const { error: deleteError } = await supabase
        .from('fund_disbursements')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setDisbursements(prev => prev.filter(d => d.id !== id));
      setCsrNotification({ type: 'success', message: 'Fund release deleted successfully' });
      setTimeout(() => setCsrNotification(null), 3000);
    } catch (err: any) {
      console.error('Error deleting fund release:', err);
      setError(err.message || 'Failed to delete record');
    }
  };

  const handleSaveFoundationMandate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) return;

    setFoundationSaving(true);
    setError(null);

    try {
      // For single companies, we use direct inputs. For group entities, we auto-calculate from entities.
      const isSingle = companyProfile?.setup_type === 'single';
      const amountValue = isSingle
        ? (companyCsrMandate === null ? 0 : Number(companyCsrMandate))
        : companyEntities.reduce((sum, e) => sum + (Number(e.primary_account_budget) || 0), 0);

      const adminValue = primaryAdminBudgetInput === '' ? 0 : Number(primaryAdminBudgetInput);

      const { error: updateError } = await supabase
        .from('companies')
        .update({
          csr_mandate: amountValue,
          admin_budget: adminValue
        })
        .eq('id', companyId);

      if (updateError) throw updateError;

      setCompanyCsrMandate(amountValue);
      setCsrNotification({ type: 'success', message: 'Budget updated successfully' });
      setTimeout(() => setCsrNotification(null), 3000);
    } catch (err: any) {
      console.error('Error saving foundation mandate:', err);
      setError(err.message || 'Failed to update primary budget');
    } finally {
      setFoundationSaving(false);
    }
  };


  const fetchData = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);

    try {
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (companyError) throw companyError;

      setCompanyCsrMandate(companyData?.csr_mandate ?? null);
      setPrimaryAdminBudgetInput(companyData?.admin_budget?.toString() || '0');
      if (companyData) {
        setCompanyProfile(companyData);
      }

      // Load Entities and Contributions early
      const [entitiesRes, contributionsRes] = await Promise.all([
        supabase.from('company_entities').select('*').eq('company_id', companyId),
        supabase.from('foundation_contributions').select('*, company_entities(name)').eq('company_id', companyId)
      ]);

      const entities = entitiesRes.data || [];
      const contributions = contributionsRes.data || [];

      setCompanyEntities(entities);
      setFoundationContributions(contributions);

      // Fetch admin expenses separately without the join right now to avoid schema cache issues
      const { data: adminExpensesData } = await supabase
        .from('company_admin_expenses')
        .select('*')
        .eq('company_id', companyId)
        .order('expense_date', { ascending: false });

      const mappedAdminExpenses = (adminExpensesData || []).map((exp: any) => {
        const entityMatch = entities.find((e: any) => e.id === exp.entity_id);
        return {
          ...exp,
          company_entities: entityMatch ? { name: entityMatch.name } : undefined
        };
      });
      setAdminExpenses(mappedAdminExpenses);

      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select(`
          id, 
          name, 
          budget, 
          tranche_plan, 
          company_id, 
          implementation_type,
          project_ngos(ngos(legal_name))
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (projectsError) throw projectsError;
      setProjects(projectsData || []);

      const projectIds = (projectsData || []).map((p: any) => p.id);

      if (projectIds.length > 0) {
        const { data: requestsData, error: requestsError } = await supabase
          .from('fund_requests')
          .select(`
          id,
          project_id,
          ngo_id,
          tranche_number,
          amount,
          purpose,
          status,
          attachments,
          clarification_request,
          rejected_reason,
          created_at,
          projects:project_id ( name ),
          ngos:ngo_id ( legal_name )
          `)
          .in('project_id', projectIds)
          .order('created_at', { ascending: false });

        if (!requestsError && requestsData) {
          const mappedRequests: FundRequest[] = requestsData.map((req: any) => ({
            id: req.id,
            project_id: req.project_id,
            ngo_id: req.ngo_id,
            tranche_number: req.tranche_number,
            amount: req.amount,
            purpose: req.purpose,
            status: req.status,
            attachments: req.attachments,
            clarification_request: req.clarification_request,
            rejected_reason: req.rejected_reason,
            created_at: req.created_at,
            projects: Array.isArray(req.projects) && req.projects.length > 0 ? req.projects[0] : { name: 'Project' },
            ngos: Array.isArray(req.ngos) && req.ngos.length > 0 ? req.ngos[0] : { legal_name: 'NGO' }
          }));
          setFundRequests(mappedRequests);
        }

        let disbursementsData: any[] | null = null;
        let disbursementsError: any = null;

        if (projectIds.length > 0) {
          const disbursementsResult = await supabase
            .from('fund_disbursements')
            .select(`
              id,
              project_id,
              ngo_id,
              fund_request_id,
              amount,
              notes,
              released_at,
              routing_method,
              projects:project_id ( name, implementation_type ),
              ngos:ngo_id ( legal_name ),
              company_entities:entity_id ( name )
            `)
            .in('project_id', projectIds)
            .order('released_at', { ascending: false });

          disbursementsData = disbursementsResult.data;
          disbursementsError = disbursementsResult.error;
        } else {
          disbursementsData = [];
        }

        if (projectIds.length > 0) {
          const { data: receiptsData, error: receiptsError } = await supabase
            .from('fund_receipts')
            .select(`
              id,
              project_id,
              ngo_id,
              tranche_number,
              receipt_date,
              amount,
              receipt_file_url,
              notes,
              uploaded_at,
              projects:project_id ( name ),
              ngos:ngo_id ( legal_name )
            `)
            .in('project_id', projectIds)
            .order('uploaded_at', { ascending: false });

          if (receiptsError) {
            console.error('Error fetching receipts:', receiptsError);
          } else {
            const mappedReceipts = (receiptsData || []).map((rec: any) => ({
              ...rec,
              project_name: Array.isArray(rec.projects) && rec.projects.length > 0
                ? rec.projects[0].name
                : rec.projects?.name || 'Project',
              ngo_name: Array.isArray(rec.ngos) && rec.ngos.length > 0
                ? rec.ngos[0].legal_name
                : rec.ngos?.legal_name || 'NGO'
            }));
            setReceipts(mappedReceipts);
          }
        } else {
          setReceipts([]);
        }


        if (disbursementsError) {
          console.error('Error fetching fund disbursements:', disbursementsError);
          setError(`Failed to load fund disbursements: ${disbursementsError.message}`);
        } else if (disbursementsData) {
          const mappedDisbursements: FundDisbursement[] = disbursementsData.map((disb: any) => {
            let sourceEntityName = companyProfile?.setup_type === 'single' ? companyProfile?.name : 'Foundation Account';
            if (disb.company_entities) {
              const entity = Array.isArray(disb.company_entities) && disb.company_entities.length > 0 ? disb.company_entities[0] : disb.company_entities;
              sourceEntityName = entity.name;
            } else {
              sourceEntityName = companyProfile?.setup_type === 'single' ? companyProfile?.name : 'Foundation Account';
            }

            return {
              id: disb.id,
              project_id: disb.project_id,
              ngo_id: disb.ngo_id,
              fund_request_id: disb.fund_request_id,
              amount: disb.amount,
              released_at: disb.released_at || new Date().toISOString(),
              notes: disb.notes,
              routing_method: disb.routing_method,
              projects: Array.isArray(disb.projects) && disb.projects.length > 0 ? disb.projects[0] : (disb.projects || { name: 'Project' }),
              ngos: Array.isArray(disb.ngos) && disb.ngos.length > 0 ? disb.ngos[0] : (disb.ngos || { legal_name: 'NGO' }),
              company_entities: sourceEntityName as unknown as any // Using this to hold the label
            }
          });
          setDisbursements(mappedDisbursements);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const handleViewDoc = async (e: React.MouseEvent, url: string, download: boolean = false) => {
    e.preventDefault();
    if (!url) return;

    if (!url.includes('/storage/v1/object/public/')) {
      window.open(url, '_blank');
      return;
    }

    try {
      const parts = url.split('/storage/v1/object/public/');
      const afterPublic = parts[1];
      const pathParts = afterPublic.split('/');

      if (pathParts.length > 1) {
        const bucketName = pathParts[0];
        const pathInBucket = pathParts.slice(1).join('/');

        const { data } = await supabase.storage.from(bucketName).createSignedUrl(decodeURIComponent(pathInBucket), 3600);

        if (data && data.signedUrl) {
          if (download) {
            const a = document.createElement('a');
            a.href = data.signedUrl;
            a.download = '';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          } else {
            window.open(data.signedUrl, '_blank');
          }
          return;
        }
      }
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error opening document:', err);
      window.open(url, '_blank');
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchData();
    }
  }, [companyId, fetchData]);

  const overviewStats = {
    totalProjects: projects.length,
    totalBudget: projects.reduce((sum, p) => sum + (p.budget || 0), 0),
    totalEntitiesMandate: companyProfile?.setup_type === 'single'
      ? (companyProfile?.csr_mandate || 0)
      : companyEntities.reduce((sum, e) => sum + (e.csr_mandate || 0), 0),
    totalOverallMandate: companyProfile?.setup_type === 'single'
      ? (companyProfile?.csr_mandate || 0)
      : companyEntities.reduce((sum, e) => sum + (e.csr_mandate || 0), 0),
    totalAdminExpenses: adminExpenses.reduce((sum, e) => sum + (e.amount || 0), 0),
    pendingRequests: fundRequests.filter(r => r.status === 'pending').length,
    approvedRequests: fundRequests.filter(r => r.status === 'approved').length,
    totalProjectDisbursed: disbursements.reduce((sum, d) => sum + (d.amount || 0), 0),
    totalOverallDisbursed: disbursements.reduce((sum, d) => sum + (d.amount || 0), 0) + adminExpenses.reduce((sum, e) => sum + (e.amount || 0), 0),
    pendingRequestsAmount: fundRequests.filter(r => r.status === 'pending').reduce((sum, r) => sum + (r.amount || 0), 0)
  };

  const financeSnapshot = useMemo(() => {
    const totalRequestedAmount = fundRequests.reduce((sum, request) => sum + (request.amount || 0), 0);
    const statusTotals = fundRequests.reduce<Record<string, { count: number; amount: number }>>((acc, request) => {
      const status = request.status || 'pending';
      if (!acc[status]) {
        acc[status] = { count: 0, amount: 0 };
      }
      acc[status].count += 1;
      acc[status].amount += request.amount || 0;
      return acc;
    }, {});

    const totalReceiptsAmount = receipts.reduce((sum, receipt) => sum + (receipt.amount || 0), 0);

    const summaryBars = [
      { label: 'Allocated Budget', value: overviewStats.totalBudget, color: '#0f766e' },
      { label: 'Released (Disbursed)', value: overviewStats.totalOverallDisbursed, color: '#3b82f6' },
      { label: 'Receipts Logged', value: totalReceiptsAmount, color: '#f97316' }
    ];

    const barMaxValue = Math.max(...summaryBars.map(bar => bar.value), 1);

    const requestStatusMeta: Record<string, { label: string; color: string }> = {
      approved: { label: 'Approved', color: '#22c55e' },
      pending: { label: 'Pending', color: '#f59e0b' },
      clarification_requested: { label: 'Clarification', color: '#f97316' },
      rejected: { label: 'Rejected', color: '#ef4444' }
    };

    const requestStatusData = Object.entries(requestStatusMeta).map(([status, meta]) => {
      const totals = statusTotals[status] || { count: 0, amount: 0 };
      return {
        name: meta.label,
        value: totals.count,
        color: meta.color,
        amount: totals.amount
      };
    }).filter(item => item.value > 0);

    const requestMaxAmount = Math.max(...requestStatusData.map(item => item.amount), 1);

    const disbursementMap = new Map<string, { label: string; value: number; sortValue: number }>();
    disbursements.forEach(disbursement => {
      const date = disbursement.released_at ? new Date(disbursement.released_at) : new Date();
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const key = `${monthStart.getFullYear()}-${monthStart.getMonth()}`;
      const label = `${monthStart.toLocaleString('en-US', { month: 'short' })} '${monthStart.getFullYear().toString().slice(-2)}`;
      const current = disbursementMap.get(key) || { label, value: 0, sortValue: monthStart.getTime() };
      current.value += typeof disbursement.amount === 'string' ? parseFloat(disbursement.amount) : (disbursement.amount || 0);
      disbursementMap.set(key, current);
    });

    const disbursementTrend = Array.from(disbursementMap.values())
      .sort((a, b) => a.sortValue - b.sortValue)
      .slice(-6);

    if (disbursementTrend.length === 0) {
      disbursementTrend.push({ label: 'No Data', value: 0, sortValue: 0 });
    }

    const disbursementMax = Math.max(...disbursementTrend.map(point => point.value), 1);

    return {
      totalRequestedAmount,
      totalReceiptsAmount,
      summaryBars,
      barMaxValue,
      requestStatusData,
      requestMaxAmount,
      disbursementTrend,
      disbursementMax,
      entityBreakdownData: companyEntities.map(e => ({
        name: e.name,
        mandate: e.csr_mandate || 0
      })),
      ngoBudgetData: projects.reduce((acc: any[], project: any) => {
        let ngoName = 'Direct Implementation';
        if (project.implementation_type === 'ngo_led' && project.project_ngos) {
          const projectNgos = Array.isArray(project.project_ngos) ? project.project_ngos : [project.project_ngos];
          if (projectNgos.length > 0 && projectNgos[0].ngos) {
            ngoName = projectNgos[0].ngos.legal_name || 'NGO';
          }
        }
        
        const existing = acc.find(item => item.name === ngoName);
        if (existing) {
          existing.budget += (project.budget || 0);
        } else {
          acc.push({ name: ngoName, budget: (project.budget || 0) });
        }
        return acc;
      }, []).filter(item => item.budget > 0)
    };
  }, [fundRequests, receipts, disbursements, overviewStats.totalBudget, overviewStats.totalOverallDisbursed, overviewStats.pendingRequestsAmount, companyEntities, companyCsrMandate]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="finance-page-container">
      <h1 className="finance-page-title" style={{ marginBottom: '1rem' }}>Finance</h1>

      <div style={{
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        color: '#1e3a8a',
        padding: '1rem 1.5rem',
        borderRadius: '8px',
        fontSize: '0.9rem',
        marginBottom: '2rem',
        lineHeight: '1.6'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>💡</span>
          <strong style={{ fontSize: '1rem' }}>Finance Workflow Guide</strong>
        </div>
        <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
          <li><strong>Budget Approval:</strong> Review and approve overall project budgets submitted by NGOs.</li>
          <li><strong>Fund Requests:</strong> Review and process individual tranche release requests.</li>
          <li><strong>Fund Releases:</strong> Record and track the actual funds you have disbursed.</li>
          <li><strong>Receipts:</strong> Track official payment receipts uploaded by NGOs.</li>
        </ul>
      </div>

      {error && (
        <div className="toast toast-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div className="finance-tabs-bar">
        {(['budgets', 'requests', 'disbursements', 'receipts'] as const)
          .map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`finance-tab-btn ${activeTab === tab ? 'active' : ''}`}
            >
              {tab === 'budgets' ? 'Budget Approval' :
                  tab === 'requests' ? 'Fund Requests' :
                    tab === 'disbursements' ? 'Fund Releases' :
                      tab === 'receipts' ? 'Receipts' : ''}
            </button>
          ))}
      </div>

      {/* Budget Approval Tab */}
      {activeTab === 'budgets' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2>Budget Approval</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>
              View and approve NGO-submitted project budgets.
            </p>
          </div>

          {projects.length === 0 ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>No projects found.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {projects.map((project) => (
                <div key={project.id} className="card">
                  <h3>{project.name}</h3>
                  <div style={{ marginTop: '1rem' }}>
                    <strong>Total Budget:</strong>
                    <p style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--primary)' }}>
                      {currencyFormatter.format(project.budget || 0)}
                    </p>
                  </div>
                  {project.tranche_plan && Array.isArray(project.tranche_plan) && project.tranche_plan.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <strong>Tranche Plan:</strong>
                      <div className="table-container" style={{ marginTop: '0.5rem' }}>
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Tranche</th>
                              <th>Amount</th>
                              <th>Planned Date</th>
                              <th>Conditions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {project.tranche_plan.map((tranche: any, idx: number) => (
                              <tr key={idx}>
                                <td>Tranche {tranche.tranche_number || idx + 1}</td>
                                <td>{currencyFormatter.format(tranche.amount || 0)}</td>
                                <td>{tranche.planned_date ? new Date(tranche.planned_date).toLocaleDateString() : 'N/A'}</td>
                                <td>{tranche.conditions || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fund Requests Tab */}
      {activeTab === 'requests' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2>Fund Request Review</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>
              Review and approve/reject fund requests from NGOs.
            </p>
          </div>

          {fundRequests.length === 0 ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>No fund requests found.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {fundRequests.map((request) => (
                <div key={request.id} className="card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                    <div>
                      <h3>{request.projects?.name || 'Project'}</h3>
                      <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                        {request.ngos?.legal_name || 'NGO'} • Tranche {request.tranche_number}
                      </p>
                    </div>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      backgroundColor: request.status === 'approved' ? '#10b98115' : request.status === 'rejected' ? '#ef444415' : '#f59e0b15',
                      color: request.status === 'approved' ? '#10b981' : request.status === 'rejected' ? '#ef4444' : '#f59e0b',
                      fontWeight: '600'
                    }}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Requested Amount:</strong>
                    <p style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--primary)' }}>
                      {currencyFormatter.format(request.amount)}
                    </p>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Purpose:</strong>
                    <p>{request.purpose}</p>
                  </div>

                  {request.attachments && request.attachments.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Attachments:</strong>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                        {request.attachments.map((url: string, idx: number) => (
                          <button
                            key={idx}
                            onClick={(e) => handleViewDoc(e, url)}
                            className="btn btn-sm btn-outline"
                          >
                            View Attachment {idx + 1}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {request.clarification_request && (
                    <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#fef3c7', borderRadius: '8px', border: '1px solid #f59e0b' }}>
                      <strong>Clarification Request:</strong>
                      <p style={{ marginTop: '0.5rem' }}>{request.clarification_request}</p>
                    </div>
                  )}
                  {request.rejected_reason && (
                    <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#fee2e2', borderRadius: '8px', border: '1px solid #ef4444' }}>
                      <strong>Rejection Reason:</strong>
                      <p style={{ marginTop: '0.5rem' }}>{request.rejected_reason}</p>
                    </div>
                  )}

                  {(request.status === 'pending' || request.status === 'clarification_requested') && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Link
                        to={`/client/company/fund-requests/${request.id}/review`}
                        className="btn btn-primary"
                      >
                        Review Request
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fund Releases Tab */}
      {activeTab === 'disbursements' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2>Fund Releases</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>
              Track all fund releases to NGOs. Fund releases are automatically created when you approve a fund request.
            </p>
            <div style={{
              padding: '0.75rem',
              background: '#f0f9ff',
              borderRadius: '8px',
              border: '1px solid #3b82f6',
              fontSize: '0.875rem',
              color: '#1e40af'
            }}>
              Each approved fund request automatically creates a disbursement record.
            </div>

          </div>

          {disbursements.length === 0 ? (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>No fund releases found.</p>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>PROJECT</th>
                      <th>NGO / VENDOR</th>
                      <th>SOURCE COMPANY</th>
                      <th style={{ textAlign: 'right' }}>AMOUNT</th>
                      <th>DATE</th>
                      <th>NOTES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {disbursements
                      .filter(d => d.ngo_id !== null)
                      .slice(0, showAllDisbursements ? disbursements.length : 4)
                      .map((disb) => (
                      <tr key={disb.id}>
                        <td style={{ fontWeight: 500 }}>{disb.projects?.name || 'N/A'}</td>
                        <td>{disb.ngos?.legal_name || 'NGO'}</td>
                        <td>
                          {disb.company_entities ? (
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              backgroundColor: String(disb.company_entities).includes('Foundation') || (companyProfile?.setup_type === 'single') ? '#10b98115' : '#3b82f615',
                              color: String(disb.company_entities).includes('Foundation') || (companyProfile?.setup_type === 'single') ? '#10b981' : '#3b82f6',
                              display: 'inline-block'
                            }}>
                              {String(disb.company_entities)}
                            </span>
                          ) : (
                            <span style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                backgroundColor: '#10b98115',
                                color: '#10b981',
                                display: 'inline-block'
                            }}>
                              {companyProfile?.setup_type === 'single' ? companyProfile?.name : 'Foundation Account'}
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{currencyFormatter.format(disb.amount || 0)}</td>
                        <td>{new Date(disb.released_at).toLocaleDateString('en-IN')}</td>
                        <td>{disb.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {disbursements.length > 4 && (
                <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                  <button
                    className="btn btn-outline"
                    onClick={() => setShowAllDisbursements(!showAllDisbursements)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    {showAllDisbursements ? 'Show Less' : `Show More (${disbursements.length - 4} more)`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Receipts Tab */}
      {activeTab === 'receipts' && (
        <div>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2>Fund Receipt Acknowledgement</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>
              View NGO-submitted receipt acknowledgements for released funds.
            </p>
          </div>
          {receipts.length > 0 ? (
            <div className="card">
              <h3 style={{ marginBottom: '1rem' }}>Receipts ({receipts.length})</h3>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {receipts.map((receipt) => (
                  <div key={receipt.id} style={{
                    padding: '1rem',
                    background: 'var(--background)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-light)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                          <strong>{receipt.project_name}</strong>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            backgroundColor: '#10b98115',
                            color: '#10b981'
                          }}>
                            {receipt.ngo_name}
                          </span>
                          {receipt.tranche_number && (
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              backgroundColor: '#3b82f615',
                              color: '#3b82f6'
                            }}>
                              Tranche {receipt.tranche_number}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          <div>
                            <strong>Date:</strong> {new Date(receipt.receipt_date).toLocaleDateString('en-IN')}
                          </div>
                          <div>
                            <strong>Amount:</strong> ₹{parseFloat(receipt.amount).toLocaleString('en-IN')}
                          </div>
                          <div>
                            <strong>Uploaded:</strong> {new Date(receipt.uploaded_at).toLocaleDateString('en-IN')}
                          </div>
                        </div>
                        {receipt.notes && (
                          <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                            {receipt.notes}
                          </p>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <a
                          href={receipt.receipt_file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline"
                        >
                          View
                        </a>
                        <a
                          href={receipt.receipt_file_url}
                          download
                          className="btn btn-sm btn-secondary"
                        >
                          Download
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--muted)' }}>Receipt acknowledgements will appear here once NGOs upload them.</p>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default CompanyFinance;